import * as Sentry from "@sentry/react";
import { Link, Stack, Typography } from "@mui/material";
import { createFileRoute, isRedirect, redirect } from "@tanstack/react-router";
import z from "zod";
import { OnboardingPage } from "@/components/auth/onboarding-page";
import { HeroImage } from "@/components/auth/hero-image";

const schema = z.object({
  token: z.string().optional(),
  oauth_error: z.string().optional().catch(undefined),
  error_code: z.string().optional().catch(undefined),
  error_subcode: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/finish-signup")({
  component: RouteComponent,
  validateSearch: schema,
  beforeLoad: async ({ search, context }) => {
    if (search.token) {
      try {
        const signInAttempt = await context.clerk.client.signIn.create({
          strategy: "ticket",
          ticket: search.token,
        });

        if (signInAttempt.status === "complete") {
          await context.clerk.setActive({
            session: signInAttempt.createdSessionId,
          });
          await context.auth.getToken({ skipCache: true });
          throw redirect({ to: "/onboarding/about-you" });
        } else {
          Sentry.captureException(signInAttempt);
        }
      } catch (err) {
        if (isRedirect(err)) {
          throw err;
        }
        Sentry.captureException(err);
      }
    }
  },
});

function RouteComponent() {
  Sentry.captureException("No sign in token");
  const search = Route.useSearch();
  if (search.error_code == "access_denied") {
    return (
      <OnboardingPage
        page={
          <Stack spacing={4}>
            <Typography variant="h2">Permission not granted</Typography>
            <Typography>
              Couldn't set up account, permission to access Xero data not granted. If you want, you
              can{" "}
              <Link href={`${import.meta.env.VITE_API_URL}/api/auth/signup/xero`}>try again</Link>.
            </Typography>
            <Typography>
              If you'd like help, <Link href="mailto:hi@trove.works">send an email</Link> or{" "}
              <Link href="https://calendly.com/anna-trove/30min">book a call</Link>.
            </Typography>
          </Stack>
        }
        hero=<HeroImage
          url="/assets/onboarding-1.svg"
          copy="Track and collect your unpaid invoices with Trove"
        />
      />
    );
  }
  return (
    <OnboardingPage
      page={
        <Stack spacing={4}>
          <Typography variant="h2">Something went wrong</Typography>
          <Typography>
            There was an error during signup. If you want, you can{" "}
            <Link href={`${import.meta.env.VITE_API_URL}/api/auth/signup/xero`}>try again</Link>.
          </Typography>
          <Typography>
            If you'd like help, <Link href="mailto:hi@trove.works">send an email</Link> or{" "}
            <Link href="https://calendly.com/anna-trove/30min">book a call</Link>.
          </Typography>
        </Stack>
      }
      hero=<HeroImage
        url="/assets/onboarding-1.svg"
        copy="Track and collect your unpaid invoices with Trove"
      />
    />
  );
}
